import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { useFirestore } from "../../firebase/FirestoreContext";
import "../../styles/global.css";
import NavigationSimple from "../../elements/NavigationSimple";
import ListItem from "../../elements/ListItem";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import Footer from "../../elements/Footer";
import FeaturedCarousel_MobileV2 from "../../elements/FeaturedCarousel_MobileV2";
import FeaturedCarousel_DesktopV2 from "../../elements/FeaturedCarousel_DesktopV2";
import Header from "../../elements/Header";
import useWindowSize from "../../utils/useWindowSize";
import FAQ from "../../elements/FAQ";
import isProductAvailable from "../../functions/isProductAvailable";
import getListingsCollection from "../../utils/getListingsCollection";

const normalizeString = (str) => {
  return str.toLowerCase().replace(/-/g, " ").trim();
};
const SearchPage = () => {
  // Get search term from the URL parameter.
  const { searchTerm } = useParams();
  const normalizedTerm = searchTerm ? normalizeString(searchTerm) : "";

  const db = useFirestore();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Environment configuration: determine which collectionPath to use.
  const environment = useContext(EnvContext);
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  // Fetch products from Firestore.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      getListingsCollection(db),
      (snapshot) => {
        let allProducts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter products based on the normalized search term against both product_name and brand_name.
        const filtered = normalizedTerm
          ? allProducts.filter((product) => {
            const productName = product.product_name ? normalizeString(product.product_name) : "";
            const brandName = product.brand_name ? normalizeString(product.brand_name) : "";
            return productName.includes(normalizedTerm) || brandName.includes(normalizedTerm);
          })
          : [];

        // Sort products: available products first, then by order_category_page.
        filtered.sort((a, b) => {
          const aUnavailable = !isProductAvailable(a);
          const bUnavailable = !isProductAvailable(b);

          if (aUnavailable && !bUnavailable) return 1;
          if (!aUnavailable && bUnavailable) return -1;

          const orderA = a.order_category_page || 0;
          const orderB = b.order_category_page || 0;

          if (orderA === 0 && orderB !== 0) return 1;
          if (orderA !== 0 && orderB === 0) return -1;
          if (orderA !== 0 && orderB !== 0) return orderA - orderB;

          return 0;
        });

        setProducts(filtered);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, normalizedTerm, collectionPath]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const formatTitle = (str) => {
    if (!str) return "";
    //
    const replaced = str.replace(/-/g, " ");
    //
    return replaced
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Use the search term to build the title for navigation.
  const navigationTitle = `Search: ${formatTitle(searchTerm)}`;

  return (
    <>
      <Header search={false} />
      <div css={{ maxWidth: "1140px", margin: "0 auto" }}>
        <div style={{ height: 35 }} />
        <NavigationSimple categoryTitle={navigationTitle} />
        <div style={{ height: 10 }} />
        {/* Header / Featured Carousel */}
        {isMobile ? (
          <FeaturedCarousel_MobileV2 featuredCollection={`search_${normalizedTerm}_1`} />
        ) : (
          <FeaturedCarousel_DesktopV2 featuredCollection={`search_${normalizedTerm}_1`} />
        )}

        {/* Content */}
        <div style={{ height: 20 }} />
        <div style={{ display: "flex", flex: 1, flexDirection: "column", width: "100%" }}>
          <div style={styles.productGrid}>
            {products.length > 0 && products.map((product) => <ListItem key={product.id} product={product} displayType={"horisontal"} />)}
          </div>
          {products.length < 1 && <div style={styles.noProducts}>No products found for your search.</div>}
        </div>
        <div style={{ height: 100 }} />
        <FAQ />
      </div>
      <Footer />
    </>
  );
};

const styles = {
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    padding: "0px 0px",
    flex: 1,
    alignContent: "start",
  },
  noProducts: {
    textAlign: "center",
    padding: "50px 20px",
    fontSize: "18px",
    opacity: 0.7,
  },
  backButton: {
    height: "50px",
    width: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "5px 0px 0 15px",
    background: "rgba(100,100,100,.07)",
    cursor: "pointer",
    borderRadius: "50%",
    paddingTop: "5px",
  },
};

export default SearchPage;
